import React, { useEffect } from 'react';
import { Box, Typography, TableRow, TableCell, IconButton } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useNavigate, useParams } from 'react-router';
import { useAppDispatch, useAppSelector } from 'store/store';
import { fetchDrawings, deleteDrawing } from 'store/actions/drawingActions';
import { usePermissions } from 'hooks/usePermissions';
import { StyledTable } from 'components/StyledTable/StyledTable';
import { useTableSort, type SortableColumn } from 'components/StyledTable/useTableSort';
import { DeleteDialog } from 'components/dialog/DeleteDialog';
import { formatDate } from 'utility/formatDate';
import { formatCurrencyEuro } from 'utility/formatCurrency';
import { WorkflowStatusId } from 'types/enums/WorkflowStatus';
import { isFulfilled } from '@reduxjs/toolkit';
import { PageHeader } from 'components/Layout/PageHeader';
import { CustomMenu, type CustomMenuRow } from 'components/CustomMenu/CustomMenu';
import { DrawingSummaryPopover } from 'components/DrawingSummaryPopover/DrawingSummaryPopover';
import { CellTrigger } from 'components/StyledTable/CellTrigger';
import { useWorkflowActions } from 'components/Workflow/useWorkflowActions';
import { WorkflowHistoryPopover } from 'components/Workflow/WorkflowHistoryPopover';
import { WorkflowStatusText, workflowStatusLabel } from 'components/Workflow/WorkflowStatusText';
import { fetchWorkflowHistory } from 'store/actions/workflowActions';
import { IDrawing } from 'types/IDrawing';
import { isRsgvLottery } from 'utility/rsgvLottery';
import { LotteryTypeId } from 'types/enums/LotteryType';
import { apiGetDrawingTickets, apiUploadDrawingTickets } from 'API/lotteryApi';
import { downloadTicketPdf } from 'utility/ticketPdf';
import type { ApiError } from 'API/apiClient';
import { addAlert } from 'store/reducers/alertReducer';
import { canEditDrawing } from 'utility/drawingEditable';

const columns: SortableColumn<IDrawing>[] = [
  { name: 'Ziehungsdatum', width: '20%', sortBy: (drawing) => drawing.drawingDate },
  { name: 'Ziehungstyp', width: '20%', sortBy: (drawing) => drawing.drawingBase?.drawingType?.name },
  { name: 'Gewinnsumme', width: '20%', sortBy: (drawing) => drawing.totalAmount ?? 0 },
  { name: 'Ziehungsbasis', width: '20%', sortBy: (drawing) => drawing.drawingBase?.name },
  { name: 'Workflow', width: '20%', sortBy: (drawing) => workflowStatusLabel(drawing.workflowDrawings?.[0]?.workflowStatusId) },
  { name: 'Aktionen', width: '1%' },
];

export const DrawingsListPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { lotteryId } = useParams<{ lotteryId: string }>();
  const { drawings, loading } = useAppSelector((state) => state.drawings);
  const { rows: sortedDrawings, columns: tableColumns, sort } = useTableSort(columns, drawings);
  const { isRedakteur } = usePermissions();
  const reloadDrawings = () => {
    if (lotteryId) dispatch(fetchDrawings({ lotteryId: Number(lotteryId) }));
  };
  const workflow = useWorkflowActions(reloadDrawings);
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [selectedDrawingId, setSelectedDrawingId] = React.useState<number | null>(null);
  const [menuAnchor, setMenuAnchor] = React.useState<HTMLElement | null>(null);
  const [menuDrawing, setMenuDrawing] = React.useState<IDrawing | null>(null);
  const [summaryAnchor, setSummaryAnchor] = React.useState<HTMLElement | null>(null);
  const [summaryDrawingId, setSummaryDrawingId] = React.useState<number | null>(null);
  const [historyAnchor, setHistoryAnchor] = React.useState<HTMLElement | null>(null);

  const openHistory = (drawingId: number) => (anchor: HTMLElement) => {
    dispatch(fetchWorkflowHistory(drawingId));
    setHistoryAnchor(anchor);
  };
  const closeSummary = () => {
    setSummaryAnchor(null);
    setSummaryDrawingId(null);
  };

  // The text of every data cell opens that row's overview.
  const openSummary = (drawingId: number) => (anchor: HTMLElement) => {
    setSummaryAnchor(anchor);
    setSummaryDrawingId(drawingId);
  };

  useEffect(() => {
    if (lotteryId) {
      dispatch(fetchDrawings({ lotteryId: Number(lotteryId) }));
    }
  }, [dispatch, lotteryId]);

  const handleDelete = async () => {
    if (selectedDrawingId) {
      const result = await dispatch(deleteDrawing(selectedDrawingId));
      if (isFulfilled(result)) {
        setDeleteDialogOpen(false);
        setSelectedDrawingId(null);
      }
    }
  };

  const uploadTicketFile = async (drawingId: number, file: File) => {
    try {
      const { count } = await apiUploadDrawingTickets(drawingId, file);
      dispatch(addAlert({ message: `${count} Lose wurden importiert`, severity: 'success' }));
      reloadDrawings();
    } catch (error) {
      dispatch(addAlert({ message: (error as ApiError).message, severity: 'warning' }));
    }
  };

  const pickTicketFile = (drawingId: number) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.csv,text/csv,text/plain';
    input.onchange = () => {
      const file = input.files?.[0];
      if (file) void uploadTicketFile(drawingId, file);
    };
    input.click();
  };

  const downloadTickets = async (drawing: IDrawing) => {
    try {
      const tickets = await apiGetDrawingTickets(drawing.id);
      downloadTicketPdf(tickets, drawing.drawingBase?.drawingType?.name ?? 'Ziehung', drawing.drawingDate);
    } catch (error) {
      dispatch(addAlert({ message: (error as ApiError).message, severity: 'warning' }));
    }
  };

  const menuItemsFor = (drawing: IDrawing): CustomMenuRow[] => {
    const drawingPath = `/lottery/${lotteryId}/drawings/${drawing.id}`;
    const status = drawing.workflowDrawings?.[0]?.workflowStatusId;
    const rows: CustomMenuRow[] = [];

    if (isRedakteur()) {
      if (canEditDrawing(status)) {
        rows.push({ name: 'Ziehung editieren', onClick: () => navigate(`${drawingPath}/edit`) });
      }
      const hasPrizes = (drawing.drawingPrizes?.length ?? 0) > 0;
      if (hasPrizes && (isRsgvLottery(lotteryId) || status === WorkflowStatusId.OFFEN || status === WorkflowStatusId.ZURUECKGEWIESEN)) {
        rows.push({ name: 'Gewinnanzahl', onClick: () => navigate(`${drawingPath}/prize-counts`) });
      }
    }

    if (isRedakteur() && drawing.lotteryTypeId === LotteryTypeId.ERWEITERT && canEditDrawing(status)) {
      rows.push({
        name: 'Losdatei hochladen',
        onClick: () => pickTicketFile(drawing.id),
      });
    }

    if (drawing.hasTickets) {
      rows.push({
        name: 'Die für diese Ziehung gespeicherten Lose als PDF herunterladen',
        onClick: () => void downloadTickets(drawing),
      });
    }

    rows.push(...workflow.menuItemsFor(drawing));

    if (isRedakteur() && status === WorkflowStatusId.OFFEN) {
      rows.push({
        name: 'Löschen',
        onClick: () => {
          setSelectedDrawingId(drawing.id);
          setDeleteDialogOpen(true);
        },
      });
    }

    return rows;
  };

  const buildMenuItems = (): CustomMenuRow[] => (menuDrawing ? menuItemsFor(menuDrawing) : []);

  return (
    <Box>
      <PageHeader title="Ziehungsübersicht" />

      {!loading && drawings.length === 0 && (
        <Typography variant="body1" sx={{ color: '#969696' }}>
          Keine Ziehungen vorhanden.
        </Typography>
      )}

      {drawings.length > 0 && (
        <StyledTable columns={tableColumns} sort={sort}>
          {sortedDrawings.map((drawing, index) => {
            const workflowStatus = drawing.workflowDrawings?.[0]?.workflowStatusId;
            return (
              <TableRow key={drawing.id} className={`psf-styled-table__body-row ${index === sortedDrawings.length - 1 ? 'psf-styled-table__body-last-row' : ''}`}>
                <TableCell>
                  <CellTrigger onOpen={openSummary(drawing.id)}>{drawing.drawingDate ? formatDate(drawing.drawingDate) : '-'}</CellTrigger>
                </TableCell>
                <TableCell>
                  <CellTrigger onOpen={openSummary(drawing.id)}>{drawing.drawingBase?.drawingType?.name || '-'}</CellTrigger>
                </TableCell>
                <TableCell>
                  <CellTrigger onOpen={openSummary(drawing.id)}>{formatCurrencyEuro(drawing.totalAmount ?? 0)}</CellTrigger>
                </TableCell>
                <TableCell>
                  <CellTrigger onOpen={openSummary(drawing.id)}>{drawing.drawingBase?.name || '-'}</CellTrigger>
                </TableCell>
                <TableCell>
                  {drawing.lotteryTypeId === LotteryTypeId.ERWEITERT && !drawing.hasTickets ? (
                    'Losdatei fehlt'
                  ) : (
                    <CellTrigger onOpen={openHistory(drawing.id)}>
                      <WorkflowStatusText statusId={workflowStatus} />
                    </CellTrigger>
                  )}
                </TableCell>
                <TableCell>
                  <Box className="psf-styled-table__body-cell-action-container">
                    {menuItemsFor(drawing).length > 0 && (
                      <IconButton
                        className="psf-styled-table__body-cell-action-button"
                        aria-label="Aktionen"
                        onClick={(event) => {
                          setMenuAnchor(event.currentTarget);
                          setMenuDrawing(drawing);
                        }}
                      >
                        <MoreVertIcon className="psf-styled-table__body-cell-action-button-icon" />
                      </IconButton>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            );
          })}
        </StyledTable>
      )}

      <CustomMenu
        anchorEl={menuAnchor}
        handleMenuClose={() => {
          setMenuAnchor(null);
          setMenuDrawing(null);
        }}
        withDivider
        isArrowRight
        items={buildMenuItems()}
      />

      <DrawingSummaryPopover drawingId={summaryDrawingId} anchorEl={summaryAnchor} onClose={closeSummary} />

      <WorkflowHistoryPopover anchorEl={historyAnchor} onClose={() => setHistoryAnchor(null)} />

      {workflow.dialogs}

      <DeleteDialog open={deleteDialogOpen} title="Ziehung löschen" onDelete={handleDelete} onCancel={() => setDeleteDialogOpen(false)}>
        <Typography className="psf-dialog-delete__text">Möchten Sie diese Ziehung wirklich löschen?</Typography>
      </DeleteDialog>
    </Box>
  );
};
